import React, { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Time slots: 9am to 5pm, hourly
const TIME_SLOTS = [
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '17:00', label: '5:00 PM' },
];

const SERVICE_OPTIONS = [
  { value: 'General Plumbing', label: 'General Plumbing' },
  { value: 'Roof Plumbing', label: 'Roof Plumbing' },
  { value: 'Maintenance Plumbing', label: 'Maintenance Plumbing' },
  { value: 'Skylight Installation', label: 'Skylight Installation' },
  { value: 'Other', label: 'Other (specify in notes)' },
];

const EMPTY_BOOKING = {
  name: '',
  phone: '',
  email: '',
  address: '',
  service: '',
  preferredDate: '',
  preferredTime: '',
  notes: '',
};

// Format YYYY-MM-DD for date input min/max in customer's local timezone.
const formatDateLocal = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Sunday is day 0 — not available.
const isSunday = (isoDate) => {
  if (!isoDate) return false;
  // Parse as local date (avoid UTC offset shift)
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).getDay() === 0;
};

const useBookingForm = () => {
  const [data, setData] = useState(EMPTY_BOOKING);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback((e) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (isSunday(data.preferredDate)) {
      toast.error('Sorry — we are closed on Sundays. Please pick Monday to Saturday.');
      return;
    }
    setSubmitting(true);
    try {
      // Strip empty optional fields so Pydantic EmailStr (optional) doesn't reject ""
      const payload = {
        ...data,
        email: data.email?.trim() ? data.email.trim() : null,
        address: data.address?.trim() || '',
        notes: data.notes?.trim() || '',
      };
      await axios.post(`${API}/booking`, payload);
      toast.success("Booking request sent! We'll call you to confirm.");
      setSuccess(true);
      setData(EMPTY_BOOKING);
      // Scroll the user to the success message so they see it immediately
      setTimeout(() => {
        const el = document.querySelector('[data-testid="booking-success"]');
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Something went wrong. Please call us directly.';
      toast.error(typeof msg === 'string' ? msg : 'Something went wrong. Please call us directly.');
    } finally {
      setSubmitting(false);
    }
  }, [data]);

  return { data, submitting, success, setSuccess, handleChange, handleSubmit };
};

const InputField = ({ label, name, type = 'text', value, onChange, required, placeholder, testId }) => (
  <div>
    <label htmlFor={`booking-${name}`} className="block text-slate-700 mb-1.5 font-medium text-sm">
      {label} {required && <span className="text-red-600">*</span>}
    </label>
    <input
      id={`booking-${name}`}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
    />
  </div>
);

const ScheduleFields = ({ data, onChange, minDate, maxDate }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <div>
      <label htmlFor="booking-preferredDate" className="block text-slate-700 mb-1.5 font-medium text-sm">
        Preferred Date <span className="text-red-600">*</span>
      </label>
      <div className="relative">
        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
        <input
          id="booking-preferredDate"
          type="date"
          name="preferredDate"
          value={data.preferredDate}
          onChange={onChange}
          min={minDate}
          max={maxDate}
          required
          data-testid="booking-date"
          className="w-full pl-10 pr-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        />
      </div>
      <p className="text-xs text-slate-500 mt-1">Mon–Sat only. Sundays closed.</p>
    </div>
    <div>
      <label htmlFor="booking-preferredTime" className="block text-slate-700 mb-1.5 font-medium text-sm">
        Preferred Time <span className="text-red-600">*</span>
      </label>
      <div className="relative">
        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
        <select
          id="booking-preferredTime"
          name="preferredTime"
          value={data.preferredTime}
          onChange={onChange}
          required
          data-testid="booking-time"
          className="w-full pl-10 pr-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none"
        >
          <option value="">Select a time</option>
          {TIME_SLOTS.map((slot) => (
            <option key={slot.value} value={slot.value}>{slot.label}</option>
          ))}
        </select>
      </div>
      <p className="text-xs text-slate-500 mt-1">9:00 AM – 5:00 PM</p>
    </div>
  </div>
);

const BookingSuccess = ({ onReset }) => (
  <div data-testid="booking-success" className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 text-center">
    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mb-4 shadow-md">
      <CheckCircle2 className="text-white" size={36} />
    </div>
    <h3 className="text-2xl font-bold text-slate-900 mb-2">Booking Received!</h3>
    <p className="text-slate-700 mb-6">
      Thanks for booking with AA Plumbing &amp; Roofing. We'll call you shortly to confirm your time slot.
    </p>
    <button
      onClick={onReset}
      data-testid="booking-reset"
      className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md"
    >
      Book Another Time
    </button>
  </div>
);

const BookingForm = () => {
  const { data, submitting, success, setSuccess, handleChange, handleSubmit } = useBookingForm();

  // Allow booking from tomorrow up to 90 days out
  const { minDate, maxDate } = useMemo(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const ninetyDaysOut = new Date(today);
    ninetyDaysOut.setDate(today.getDate() + 90);
    return {
      minDate: formatDateLocal(tomorrow),
      maxDate: formatDateLocal(ninetyDaysOut),
    };
  }, []);

  if (success) {
    return <BookingSuccess onReset={() => setSuccess(false)} />;
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-8 space-y-5">
      <ScheduleFields data={data} onChange={handleChange} minDate={minDate} maxDate={maxDate} />

      <div>
        <label htmlFor="booking-service" className="block text-slate-700 mb-1.5 font-medium text-sm">
          Service Required <span className="text-red-600">*</span>
        </label>
        <select
          id="booking-service"
          name="service"
          value={data.service}
          onChange={handleChange}
          required
          data-testid="booking-service"
          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        >
          <option value="">Select a service</option>
          {SERVICE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <InputField label="Your Name" name="name" value={data.name} onChange={handleChange} required placeholder="John Smith" testId="booking-name" />
        <InputField label="Phone Number" name="phone" type="tel" value={data.phone} onChange={handleChange} required placeholder="04xx xxx xxx" testId="booking-phone" />
      </div>

      <InputField label="Email (optional)" name="email" type="email" value={data.email} onChange={handleChange} placeholder="john@example.com" testId="booking-email" />
      <InputField label="Service Address" name="address" value={data.address} onChange={handleChange} placeholder="123 Main St, Melbourne" testId="booking-address" />

      <div>
        <label htmlFor="booking-notes" className="block text-slate-700 mb-1.5 font-medium text-sm">
          Notes (optional)
        </label>
        <textarea
          id="booking-notes"
          name="notes"
          value={data.notes}
          onChange={handleChange}
          rows="3"
          data-testid="booking-notes"
          placeholder="Briefly describe what you'd like done..."
          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="booking-submit"
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-md hover:shadow-lg"
      >
        {submitting ? 'Sending Booking…' : 'Request Booking'}
      </button>

      <p className="text-xs text-slate-500 text-center">
        We'll call you to confirm the time — your slot isn't locked in until we confirm.
      </p>
    </form>
  );
};

const BookingInfoPanel = () => (
  <div className="space-y-6">
    <div>
      <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
        Book a Time
      </span>
      <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
        Schedule Your Job
      </h2>
      <p className="text-lg text-slate-600 leading-relaxed">
        Pick a day and time that suits you. We'll call you back to confirm the slot and any details about the job.
      </p>
    </div>

    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
      <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
        <Clock className="text-blue-600" size={20} />
        Trading Hours
      </h3>
      <ul className="space-y-1.5 text-slate-700 text-sm">
        <li className="flex justify-between"><span>Monday – Friday</span><span className="font-semibold">9:00 AM – 5:00 PM</span></li>
        <li className="flex justify-between"><span>Saturday</span><span className="font-semibold">9:00 AM – 5:00 PM</span></li>
        <li className="flex justify-between text-slate-500"><span>Sunday</span><span className="font-semibold">Closed</span></li>
      </ul>
    </div>

    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
      <h3 className="font-bold text-slate-900 mb-3">How It Works</h3>
      <ol className="space-y-3 text-slate-700 text-sm">
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs">1</span>
          <span>Pick your preferred date and time</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs">2</span>
          <span>We get an instant booking request notification</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs">3</span>
          <span>We call you back to confirm the slot</span>
        </li>
      </ol>
    </div>
  </div>
);

const BookingSection = () => (
  <section
    id="booking"
    data-testid="booking-section"
    className="py-24 bg-gradient-to-br from-slate-50 to-blue-50"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <BookingInfoPanel />
        <BookingForm />
      </div>
    </div>
  </section>
);

export default BookingSection;
